import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Search, Filter } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { formatDate } from '../../../utils/dateUtils';

export default function LeasesAdmin() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { data: leases, isLoading } = useQuery({
    queryKey: ['admin', 'leases'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subleases')
        .select(`
          lease_id,
          lease_start,
          lease_end,
          lease_method,
          lease_type,
          lease_rent_freq,
          lease_dealing,
          lease_holder,
          leaseholders!subleases_lease_holder_fkey (
            holder_id,
            holder_name
          ),
          lease_lots!inner (
            leaselot_lot,
            leaselot_use,
            land_lots!inner (
              lot_id,
              lot_perm_use,
              lot_community
            )
          )
        `)
        .order('created_date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      return data;
    }
  });

  const filteredLeases = leases?.filter(lease => 
    lease.lease_id.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
    lease.leaseholders?.holder_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lease.lease_lots[0]?.land_lots.lot_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Leases</h1>
          <p className="mt-1 text-sm text-gray-500">
            Add, edit, and manage property leases
          </p>
        </div>
        <button className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark">
          <Plus className="h-5 w-5 mr-2" />
          Add Lease
        </button>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search leases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
        </div>
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <Filter className="h-5 w-5" />
        </button>
      </div>

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lot Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Leaseholder
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Community
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Start Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  End Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredLeases?.map((lease) => (
                <tr key={lease.lease_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {lease.lease_lots[0]?.land_lots.lot_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lease.leaseholders?.holder_name || 'Unassigned'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {lease.lease_lots[0]?.land_lots.lot_community || 'Not specified'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(lease.lease_start)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(lease.lease_end)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      lease.lease_end < new Date().toISOString()
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {lease.lease_end < new Date().toISOString() ? 'Expired' : 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button className="text-primary hover:text-primary-dark">
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}